import type { InspectionDto, InspectionShotDto, InspectionShotKey } from '@carvia/ros-client-types';

export const VEHICLE_INSPECTION_FLAG = 'vehicle-inspection';

/** Mirrors the backend minimum for a skip reason. */
export const MIN_SKIP_REASON_LENGTH = 10;

export type InspectionShotState = 'open' | 'captured' | 'skipped';

export type InspectionShotView = {
  key: InspectionShotKey;
  state: InspectionShotState;
  shot: InspectionShotDto | null;
};

export function getShotState(shot: InspectionShotDto | null | undefined): InspectionShotState {
  if (!shot) return 'open';
  return shot.captureId ? 'captured' : 'skipped';
}

/** Expected shots in catalog order, followed by any recorded shot the list no longer knows. */
export function getInspectionShotViews(inspection: InspectionDto): InspectionShotView[] {
  const byKey = new Map(inspection.shots.map((shot) => [shot.shotKey, shot]));
  const expected = inspection.expectedShotKeys.map((key) => {
    const shot = byKey.get(key) ?? null;
    return { key, state: getShotState(shot), shot };
  });
  const extra = inspection.shots
    .filter((shot) => !inspection.expectedShotKeys.includes(shot.shotKey))
    .map((shot) => ({ key: shot.shotKey, state: getShotState(shot), shot }));
  return [...expected, ...extra];
}

export function isInspectionRecorded(inspection: InspectionDto): boolean {
  const recorded = new Set(inspection.shots.map((shot) => shot.shotKey));
  return inspection.expectedShotKeys.every((key) => recorded.has(key));
}

export function countPendingImages(inspection: InspectionDto): number {
  return inspection.shots.filter((shot) => shot.isImagePending).length;
}

/** Uploaded photo of a shot, or the one still queued on this device (see `useInspectionLocalPreviews`). */
export function getShotImageUrl(
  view: InspectionShotView,
  localPreviews?: Record<string, string>,
): string | undefined {
  return (
    view.shot?.imageFileUrl ?? (view.state === 'captured' ? localPreviews?.[view.key] : undefined)
  );
}
